import type { MappingResource } from "@prequel/import-react";
import { useDataset } from "@prequel/import-react";
import { ArrowRightIcon, Badge, BadgeColor, Table, WavesIcon } from "@prequel/shared-components";

import { useDestination } from "@/api/import/destinations";
import { useAllSources } from "@/api/import/sources";
import PipelineCards, { type PipelineCard } from "@/components/import/PipelineCards";
import ProviderCard from "@/components/import/ProviderCard";
import { schemaColumns } from "@/components/import/schemaColumns";
import { useStreamContext } from "@/pages/import/StreamsPage/StreamPage";

const MAPPING_COLUMNS = schemaColumns(["Type", "Dataset column or expression", "Destination field"]);

const mappingTypeLabel = (mapping: MappingResource) => (mapping.field_type === "expression" ? "Expression" : "Column");

const mappingSource = (mapping: MappingResource) => mapping.column?.name ?? mapping.expression?.expr ?? "-";

const StreamOverview = () => {
  const { stream } = useStreamContext();
  const { data: dataset } = useDataset(stream.dataset_id);
  const { data: destination } = useDestination(stream.destination_id);
  const { items: sources } = useAllSources();

  const source = sources.find((candidate) => candidate.id === dataset?.source_id);

  const destinationUri =
    destination?.webhook_batch?.request_template.uri ?? destination?.webhook_record?.request_template.uri;

  const cards: PipelineCard[] = [
    {
      id: "dataset",
      label: "Dataset",
      title: dataset?.name ?? stream.dataset_id,
      subtitle: source?.name,
      to: `/import/datasets/${stream.dataset_id}`,
    },
    {
      id: "stream",
      label: "Stream",
      icon: <WavesIcon className="text-stone-500" />,
      title: stream.name,
      rows: [
        {
          label: "Orchestration",
          value: (
            <Badge
              color={stream.is_enabled ? BadgeColor.GREEN : BadgeColor.GRAY}
              text={stream.is_enabled ? "Enabled" : "Disabled"}
            />
          ),
        },
      ],
    },
    {
      id: "destination",
      label: "Destination",
      title: destination?.name ?? stream.destination_id,
      subtitle: destinationUri && <span className="font-mono">{destinationUri}</span>,
      to: `/import/destinations/${stream.destination_id}`,
    },
  ];

  const mappingRows = stream.mappings.map((mapping, index) => ({
    id: `${mapping.target_field}-${index}`,
    cells: [
      { value: mappingTypeLabel(mapping) },
      {
        value: <span className="font-mono text-stone-800">{mappingSource(mapping)}</span>,
      },
      {
        value: (
          <span className="flex items-center gap-3">
            <ArrowRightIcon className="shrink-0 text-stone-400" />
            <span className="font-mono text-stone-800">{mapping.target_field}</span>
          </span>
        ),
      },
    ],
  }));

  return (
    <div className="mt-6">
      <PipelineCards cards={cards} />

      {dataset && <ProviderCard providerId={dataset.provider_id} />}

      <section className="mb-4">
        <Table
          header={<h3 className="mb-2 text-sm font-medium text-stone-800">Mappings</h3>}
          columns={MAPPING_COLUMNS}
          rows={mappingRows}
          emptyState="No mappings configured."
        />
      </section>
    </div>
  );
};

export default StreamOverview;
